import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { materialsAPI, materialCategoriesAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Minus, Search, Edit, Trash2, Package, AlertTriangle, Upload, X, Image as ImageIcon, Eye, Box, Hash, Ruler, DollarSign, FileText, StickyNote, Tag, FolderOpen, Settings2, Copy } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import PaywallDialog from '../../components/PaywallDialog';

// Material Detail Sheet Component
function MaterialDetailSheet({ material, onClose, onEdit }) {
  const [showPhotoViewer, setShowPhotoViewer] = React.useState(false);
  
  if (!material) return null;

  const isLowStock = material.quantity <= material.min_quantity;

  return (
    <>
      <Sheet open={true} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-md p-0 overflow-y-auto [&>button]:hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-8 pb-6">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-background/80 transition-colors z-10"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
            
            <div className="flex items-start gap-4 mt-2">
              {material.photo_url ? (
                <img 
                  src={material.photo_url} 
                  alt={material.name}
                  className="h-16 w-16 rounded-xl object-cover border-2 border-background shadow-sm cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowPhotoViewer(true)}
                  title="Нажмите для просмотра"
                />
              ) : (
                <div className="h-16 w-16 rounded-xl bg-primary/20 flex items-center justify-center border-2 border-background shadow-sm">
                  <Package className="h-8 w-8 text-primary" />
                </div>
              )}
              <div className="flex-1 min-w-0 pt-1">
                <h2 className="text-xl font-bold truncate">{material.name}</h2>
                {material.sku && (
                  <p className="text-sm text-muted-foreground mt-1 font-mono">
                    Арт: {material.sku}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 pt-6 pb-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className={`relative overflow-hidden rounded-xl border p-4 ${isLowStock ? 'bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20' : 'bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20'}`}>
                <Box className={`absolute -right-2 -bottom-2 h-12 w-12 ${isLowStock ? 'text-red-500/10' : 'text-blue-500/10'}`} />
                <div className="relative">
                  <p className={`text-xs font-medium uppercase tracking-wide ${isLowStock ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                    Количество {isLowStock && '⚠️'}
                  </p>
                  <p className="text-2xl font-bold mt-1">{material.quantity} {material.unit}</p>
                </div>
              </div>
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 p-4">
                <DollarSign className="absolute -right-2 -bottom-2 h-12 w-12 text-emerald-500/10" />
                <div className="relative">
                  <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Стоимость</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(material.price * material.quantity)}</p>
                </div>
              </div>
            </div>

          {/* Material Info */}
          <div className="mt-8">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
              <Hash className="h-3.5 w-3.5" />
              Информация о материале
            </h4>
            <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-violet-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Цена за единицу</p>
                    <p className="font-medium">{formatCurrency(material.price)} / {material.unit}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Минимальный остаток</p>
                    <p className="font-medium">{material.min_quantity} {material.unit}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-teal-500/10 flex items-center justify-center">
                    <Ruler className="h-4 w-4 text-teal-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Единица измерения</p>
                    <p className="font-medium">{material.unit}</p>
                  </div>
                </div>
              </div>
              {material.category && (
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                    <Tag className="h-4 w-4 text-indigo-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Категория</p>
                    <p className="font-medium">{material.category}</p>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>

          {/* Description */}
          {material.description && (
            <div className="mt-8">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <FileText className="h-3.5 w-3.5" />
                Описание
              </h4>
              <div className="p-4 rounded-xl bg-muted/50 border border-border">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{material.description}</p>
              </div>
            </div>
          )}

          {/* Notes */}
          {material.notes && (
            <div className="mt-8">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                <StickyNote className="h-3.5 w-3.5" />
                Заметки
              </h4>
              <div className="p-4 rounded-xl bg-muted/50 border border-border">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{material.notes}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8">
            <Button 
              onClick={onEdit} 
              className="w-full h-12 font-medium text-base"
              variant="default"
            >
              <Edit className="h-4 w-4 mr-2" />
              Редактировать материал
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    {/* Photo Viewer Modal */}
    {showPhotoViewer && material.photo_url && (
      <div 
        className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
        onClick={() => setShowPhotoViewer(false)}
      >
        <button 
          onClick={() => setShowPhotoViewer(false)}
          className="absolute top-4 right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
        >
          <X className="h-6 w-6 text-white" />
        </button>
        <img 
          src={material.photo_url} 
          alt={material.name}
          className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
        <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
          Нажмите в любом месте для закрытия
        </p>
      </div>
    )}
    </>
  );
}

export default function MaterialsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalMaterials, setTotalMaterials] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [lowStockCountAll, setLowStockCountAll] = useState(0);
  const PAGE_SIZE = 15;
  const loadMoreRef = useRef(null);
  const skipRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [viewingMaterial, setViewingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    quantity: 0,
    unit: 'шт',
    price: 0,
    min_quantity: 0,
    description: '',
    notes: '',
    photo_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallLimit, setPaywallLimit] = useState(1);
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // null = all
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');

  useEffect(() => {
    loadMaterials();
    loadCategories();
  }, []);

  // Handle ?view=MATERIAL_ID or ?open=MATERIAL_ID from URL
  useEffect(() => {
    const viewId = searchParams.get('view') || searchParams.get('open');
    if (viewId && !loading && materials.length > 0) {
      const mat = materials.find(m => m.id === viewId);
      if (mat) {
        setViewingMaterial(mat);
      }
      searchParams.delete('view');
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [materials, loading, searchParams, setSearchParams]);

  const loadMaterials = async () => {
    try {
      skipRef.current = 0;
      const res = await materialsAPI.getAll({ skip: 0, limit: PAGE_SIZE });
      const total = parseInt(res.headers?.['x-total-count'] || '0', 10);
      setTotalMaterials(total);
      setTotalValue(parseFloat(res.headers?.['x-total-value'] || '0'));
      setLowStockCountAll(parseInt(res.headers?.['x-low-stock-count'] || '0', 10));
      setMaterials(res.data);
      skipRef.current = res.data.length;
      setHasMore(res.data.length < total);
    } catch (error) {
      toast.error('Ошибка загрузки материалов');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreMaterials = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await materialsAPI.getAll({ skip: skipRef.current, limit: PAGE_SIZE });
      const total = parseInt(res.headers?.['x-total-count'] || '0', 10);
      setTotalMaterials(total);
      setTotalValue(parseFloat(res.headers?.['x-total-value'] || '0'));
      setLowStockCountAll(parseInt(res.headers?.['x-low-stock-count'] || '0', 10));
      setMaterials(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const newMaterials = res.data.filter(m => !existingIds.has(m.id));
        return [...prev, ...newMaterials];
      });
      skipRef.current += res.data.length;
      setHasMore(skipRef.current < total);
    } catch (error) {
      console.error('Error loading more materials:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreMaterials();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMoreMaterials]);

  const loadCategories = async () => {
    try {
      const res = await materialCategoriesAPI.getAll();
      setCategories(res.data);
    } catch { /* ignore */ }
  };

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      await materialCategoriesAPI.create({ name });
      setNewCategoryName('');
      loadCategories();
      toast.success('Категория создана');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleUpdateCategory = async (id) => {
    const name = editCategoryName.trim();
    if (!name) return;
    try {
      await materialCategoriesAPI.update(id, { name });
      setEditingCategory(null);
      setEditCategoryName('');
      loadCategories();
      loadMaterials(); // materials may have updated category name
      toast.success('Категория обновлена');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Удалить категорию? У материалов с этой категорией она будет сброшена.')) return;
    try {
      await materialCategoriesAPI.delete(id);
      if (selectedCategory && categories.find(c => c.id === id)?.name === selectedCategory) {
        setSelectedCategory(null);
      }
      loadCategories();
      loadMaterials();
      toast.success('Категория удалена');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Размер файла не должен превышать 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo_url: reader.result });
        toast.success('Фото загружено');
        setUploadingPhoto(false);
      };
      reader.onerror = () => {
        toast.error('Ошибка загрузки фото');
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Ошибка загрузки фото');
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setFormData({ ...formData, photo_url: '' });
  };

  const openDialog = (material = null) => {
    if (material) {
      setEditingMaterial(material);
      setFormData({
        name: material.name,
        sku: material.sku || '',
        quantity: material.quantity,
        unit: material.unit,
        price: material.price,
        min_quantity: material.min_quantity,
        description: material.description || '',
        notes: material.notes || '',
        photo_url: material.photo_url || '',
        category: material.category || '',
      });
    } else {
      setEditingMaterial(null);
      setFormData({
        name: '',
        sku: '',
        quantity: 0,
        unit: 'шт',
        price: 0,
        min_quantity: 0,
        description: '',
        notes: '',
        photo_url: '',
        category: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Введите название материала');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        quantity: Number(formData.quantity),
        price: Number(formData.price),
        min_quantity: Number(formData.min_quantity),
        category: formData.category || null,
      };

      if (editingMaterial) {
        await materialsAPI.update(editingMaterial.id, data);
        toast.success('Материал обновлён');
      } else {
        await materialsAPI.create(data);
        toast.success('Материал добавлен');
      }
      setDialogOpen(false);
      loadMaterials();
    } catch (error) {
      if (error.response?.status === 403) {
        setDialogOpen(false);
        const detail = error.response?.data?.detail || '';
        const match = detail.match(/(\d+)/);
        if (match) setPaywallLimit(parseInt(match[1]));
        setPaywallOpen(true);
      } else {
        toast.error('Ошибка сохранения');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const material = materials.find(m => m.id === id);
    const materialName = material?.name || 'этот материал';
    const hasStock = material?.current_stock > 0;
    const confirmText = hasStock 
      ? `Удалить материал "${materialName}"?\n\nВнимание: на складе осталось ${material.current_stock} ${material.unit}.\nЭто действие нельзя отменить.`
      : `Удалить материал "${materialName}"?\n\nЭто действие нельзя отменить.`;
    
    if (!window.confirm(confirmText)) return;
    try {
      await materialsAPI.delete(id);
      toast.success('Материал удалён');
      loadMaterials();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const quickUpdateQuantity = async (material, delta) => {
    const newQuantity = Math.max(0, material.quantity + delta);
    try {
      await materialsAPI.update(material.id, { quantity: newQuantity });
      setMaterials(prev => prev.map(m => m.id === material.id ? { ...m, quantity: newQuantity } : m));
      if (viewingMaterial?.id === material.id) {
        setViewingMaterial(prev => ({ ...prev, quantity: newQuantity }));
      }
    } catch (error) {
      toast.error('Ошибка обновления');
    }
  };

  const duplicateMaterial = async (material) => {
    try {
      await materialsAPI.create({
        name: material.name + ' (копия)',
        sku: material.sku || '',
        quantity: material.quantity,
        unit: material.unit || 'шт',
        price: material.price || 0,
        min_quantity: material.min_quantity || 0,
        description: material.description || '',
        notes: material.notes || '',
        category: material.category || '',
        photo_url: material.photo_url || '',
      });
      toast.success(`${material.name} скопирован`);
      loadMaterials();
    } catch (error) {
      toast.error('Ошибка при копировании');
    }
  };

  const filteredMaterials = materials.filter(material => {
    if (filterLowStock && material.quantity > material.min_quantity) return false;
    if (selectedCategory && material.category !== selectedCategory) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      material.name.toLowerCase().includes(query) ||
      material.sku?.toLowerCase().includes(query) ||
      material.category?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6" data-testid="materials-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Материалы</h1>
          <p className="text-sm text-muted-foreground">Управление инвентарём</p>
        </div>
        <Button onClick={() => openDialog()} data-testid="add-material-btn">
          <Plus className="h-4 w-4 mr-2" />
          Добавить материал
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="px-3 py-2 flex items-center gap-3">
            <div className="p-2 rounded-full bg-muted">
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Всего материалов</p>
              <p className="text-lg font-bold">{totalMaterials}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="px-3 py-2 flex items-center gap-3">
            <div className="p-2 rounded-full bg-muted">
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Общая стоимость</p>
              <p className="text-lg font-bold">
                {formatCurrency(totalValue)}
              </p>
            </div>
          </CardContent>
        </Card>
        {lowStockCountAll > 0 && (
          <Card className="border-destructive/50">
            <CardContent className="px-3 py-2 flex items-center gap-3">
              <div className="p-2 rounded-full bg-destructive/10">
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </div>
              <div>
                <p className="text-xs text-destructive">Заканчивается</p>
                <p className="text-lg font-bold text-destructive">{lowStockCountAll}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по названию или артикулу..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
              data-testid="search-materials-input"
            />
          </div>
          {lowStockCountAll > 0 && (
            <Button
              variant={filterLowStock ? "destructive" : "outline"}
              size="sm"
              onClick={() => setFilterLowStock(!filterLowStock)}
              className="shrink-0 gap-1.5 h-9"
              data-testid="filter-low-stock-btn"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Заканчивается</span>
              <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full text-xs font-bold bg-background/20">
                {filterLowStock ? filteredMaterials.length : lowStockCountAll}
              </span>
            </Button>
          )}
        </div>
        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs px-2.5"
              onClick={() => setSelectedCategory(null)}
            >
              Все
            </Button>
            {categories.map(cat => {
              const count = materials.filter(m => m.category === cat.name).length;
              return (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.name ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs px-2.5"
                  onClick={() => setSelectedCategory(selectedCategory === cat.name ? null : cat.name)}
                >
                  {cat.name}
                  <span className="ml-1 opacity-60">{count}</span>
                </Button>
              );
            })}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs px-2 gap-1"
              onClick={() => setShowCategoryManager(true)}
              title="Управление категориями"
            >
              <Edit className="h-3 w-3" />
              <span className="hidden sm:inline">Настроить</span>
            </Button>
          </div>
        )}
        {categories.length === 0 && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={() => setShowCategoryManager(true)}
            >
              <Tag className="h-3.5 w-3.5" />
              Добавить категории
            </Button>
          </div>
        )}
      </div>

      {/* Materials */}
      <div className="mt-2">
      {loading ? (
        <div className="p-6 space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : filteredMaterials.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-16">
              <Package className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">Нет материалов</p>
              <p className="text-muted-foreground mb-1">Добавьте первый материал</p>
              <p className="text-xs text-muted-foreground/60 mb-4">YaDela.ru — твои дела проще</p>
              <Button onClick={() => openDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Добавить материал
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile: Card view */}
          <div className="block md:hidden">
            {filteredMaterials.map((material, index, array) => {
                const isLowStock = material.quantity <= material.min_quantity;
                const isLast = index === array.length - 1;
                return (
                  <div 
                    key={material.id} 
                    className={`border rounded-lg p-4 bg-card cursor-pointer hover:border-primary/30 transition-colors ${!isLast ? 'mb-5' : ''}`}
                    data-testid={`material-row-${material.id}`}
                    onClick={() => setViewingMaterial(material)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{material.name}</span>
                          {material.category && <Badge variant="secondary" className="text-xs">{material.category}</Badge>}
                          {isLowStock && <Badge variant="destructive" className="text-xs">Мало</Badge>}
                        </div>
                        {material.sku && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            <span className="font-medium">Арт:</span>{' '}
                            <span className="font-mono">{material.sku}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewingMaterial(material)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(material)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicateMaterial(material)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(material.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => quickUpdateQuantity(material, -1)}
                          disabled={material.quantity <= 0}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </Button>
                        <span className={`font-medium min-w-[60px] text-center ${isLowStock ? 'text-destructive' : ''}`}>
                          {material.quantity} {material.unit}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => quickUpdateQuantity(material, 1)}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <span className="font-semibold">{formatCurrency(material.price * material.quantity)}</span>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Desktop: Card grid view (like Orders) */}
          <div className="hidden md:block">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredMaterials.map((material) => {
                const isLowStock = material.quantity <= material.min_quantity;
                return (
                  <div
                    key={material.id}
                    className="p-4 bg-card rounded-xl border shadow-sm hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer"
                    data-testid={`material-row-desktop-${material.id}`}
                    onClick={() => setViewingMaterial(material)}
                  >
                    {/* Top row: Name + Category + Low stock badge */}
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="font-medium text-sm truncate">{material.name}</span>
                        {material.category && <Badge variant="outline" className="text-[11px] py-0">{material.category}</Badge>}
                      </div>
                      {isLowStock && (
                        <Badge variant="destructive" className="text-[11px] py-0 shrink-0">Мало</Badge>
                      )}
                    </div>

                    {/* SKU + Description */}
                    {(material.sku || material.description) && (
                      <div className="text-[11px] text-muted-foreground mb-2 space-y-0.5">
                        {material.sku && (
                          <div>Арт: <span className="font-mono">{material.sku}</span></div>
                        )}
                        {material.description && (
                          <div className="truncate">{material.description}</div>
                        )}
                      </div>
                    )}

                    {/* Quantity controls + Price */}
                    <div className="flex items-center justify-between mb-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => quickUpdateQuantity(material, -1)}
                          disabled={material.quantity <= 0}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className={`font-medium text-sm min-w-[50px] text-center ${isLowStock ? 'text-destructive' : ''}`}>
                          {material.quantity} {material.unit}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => quickUpdateQuantity(material, 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <span className="text-sm text-muted-foreground">{formatCurrency(material.price)}/шт</span>
                    </div>

                    {/* Bottom: Total + Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <div className="font-bold text-base">{formatCurrency(material.price * material.quantity)}</div>
                      <div className="flex items-center gap-0" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setViewingMaterial(material)}
                          data-testid={`view-material-${material.id}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openDialog(material)}
                          data-testid={`edit-material-${material.id}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => duplicateMaterial(material)}
                          title="Копировать"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => handleDelete(material.id)}
                          data-testid={`delete-material-${material.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
          </div>
        </>
      )}
      </div>

      {/* Infinite scroll sentinel + counter */}
      <div ref={loadMoreRef} className="py-4 text-center">
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Загрузка...</span>
          </div>
        )}
        {!loading && !loadingMore && filteredMaterials.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Показано {filteredMaterials.length} из {totalMaterials}
            {!hasMore && totalMaterials > 0 && ' — все материалы загружены'}
          </p>
        )}
      </div>

      {/* Material Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="material-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingMaterial ? 'Редактировать материал' : 'Новый материал'}
            </DialogTitle>
            <DialogDescription>
              Заполните информацию о материале
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Название *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Название материала"
                  data-testid="material-name-input"
                  autoFocus={false}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label>Артикул</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="SKU-001"
                  data-testid="material-sku-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Количество</Label>
                <Input
                  type="number"
                  value={formData.quantity || ''}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                  data-testid="material-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Единица</Label>
                <Input
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="шт"
                  data-testid="material-unit-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Мин. остаток</Label>
                <Input
                  type="number"
                  value={formData.min_quantity || ''}
                  onChange={(e) => setFormData({ ...formData, min_quantity: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                  data-testid="material-min-quantity-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Цена за единицу</Label>
              <Input
                type="number"
                value={formData.price || ''}
                onChange={(e) => setFormData({ ...formData, price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                placeholder="0"
                data-testid="material-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Категория</Label>
              <div className="flex flex-wrap gap-1.5">
                <Button
                  type="button"
                  variant={!formData.category ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFormData({ ...formData, category: '' })}
                >
                  Без категории
                </Button>
                {categories.map(cat => (
                  <Button
                    type="button"
                    key={cat.id}
                    variant={formData.category === cat.name ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setFormData({ ...formData, category: cat.name })}
                  >
                    {cat.name}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setShowCategoryManager(true)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Новая
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Описание</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Описание материала"
                rows={2}
                data-testid="material-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Заметки</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Дополнительные заметки (например, особенности хранения, поставщик и т.д.)"
                rows={3}
                data-testid="material-notes-input"
              />
            </div>
            
            {/* Photo Upload */}
            <div className="space-y-2">
              <Label>Фото материала</Label>
              {formData.photo_url ? (
                <div className="relative">
                  <img 
                    src={formData.photo_url} 
                    alt="Материал" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={handleRemovePhoto}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-muted-foreground/50 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                    disabled={uploadingPhoto}
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <ImageIcon className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {uploadingPhoto ? 'Загрузка...' : 'Нажмите для загрузки фото'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG до 5MB</p>
                  </label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-between gap-2 pt-2">
            <Button variant="outline" size="lg" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button size="lg" onClick={handleSave} disabled={saving} data-testid="save-material-btn">
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material Detail Sheet */}
      {viewingMaterial && (
        <MaterialDetailSheet
          material={viewingMaterial}
          onClose={() => setViewingMaterial(null)}
          onEdit={() => {
            setViewingMaterial(null);
            openDialog(viewingMaterial);
          }}
        />
      )}

      {/* Paywall Dialog */}
      <PaywallDialog
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        resourceType="materials"
        limit={paywallLimit}
      />

      {/* Category Manager Dialog */}
      <Dialog open={showCategoryManager} onOpenChange={setShowCategoryManager}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Категории материалов
            </DialogTitle>
            <DialogDescription>Создавайте и управляйте категориями для группировки материалов</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Add new category */}
            <div className="flex items-center gap-3">
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Новая категория..."
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
              />
              <Button size="sm" onClick={handleAddCategory} disabled={!newCategoryName.trim()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Category list */}
            {categories.length === 0 ? (
              <div className="text-center py-8 mt-2">
                <FolderOpen className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">Нет категорий</p>
                <p className="text-xs text-muted-foreground mt-1">Создайте первую категорию выше</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto mt-2 pr-1">
                {categories.map(cat => {
                  const count = materials.filter(m => m.category === cat.name).length;
                  const isEditing = editingCategory === cat.id;
                  return (
                    <div key={cat.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-card hover:bg-muted/60 transition-colors">
                      {isEditing ? (
                        <>
                          <Input
                            value={editCategoryName}
                            onChange={(e) => setEditCategoryName(e.target.value)}
                            className="h-8 flex-1 text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateCategory(cat.id);
                              if (e.key === 'Escape') { setEditingCategory(null); setEditCategoryName(''); }
                            }}
                          />
                          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => handleUpdateCategory(cat.id)}>
                            <Tag className="h-3.5 w-3.5 text-green-500" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => { setEditingCategory(null); setEditCategoryName(''); }}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm font-medium">{cat.name}</span>
                          <Badge variant="outline" className="text-xs shrink-0">{count}</Badge>
                          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => { setEditingCategory(cat.id); setEditCategoryName(cat.name); }}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive hover:text-destructive" onClick={() => handleDeleteCategory(cat.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryManager(false)}>Закрыть</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
